import { AlertCircle, CheckCircle, Clock, XCircle, User, Building2 } from 'lucide-react';
import { LinkStatus } from '../../types/pilgrimGuide';
import { useLanguage } from '../../contexts/LanguageContext';

export type LinkingTableRowData = {
  id: string;
  pilgrimId: string;
  pilgrimName: string;
  passportNumber: string;
  gender: 'male' | 'female' | 'unknown';
  residenceCountry: string | null;
  guideName: string | null;
  organizerName: string | null;
  status: LinkStatus | 'unlinked';
  isLinked: boolean;
};

type Props = {
  row: LinkingTableRowData;
};

export const LinkingTableRow = ({ row }: Props) => {
  const { translations } = useLanguage();

  const getStatusConfig = (status: LinkingTableRowData['status']) => {
    switch (status) {
      case 'approved':
        return {
          icon: CheckCircle,
          color: 'text-green-500',
          bg: 'bg-green-50',
          label: translations?.linkingTableRow?.status?.approved || 'Approved',
        };
      case 'disputed':
        return {
          icon: AlertCircle,
          color: 'text-orange-500',
          bg: 'bg-orange-50',
          label: translations?.linkingTableRow?.status?.disputed || 'Disputed',
        };
      case 'rejected':
        return {
          icon: XCircle,
          color: 'text-red-500',
          bg: 'bg-red-50',
          label: translations?.linkingTableRow?.status?.rejected || 'Rejected',
        };
      case 'waiting_identification':
        return {
          icon: Clock,
          color: 'text-blue-500',
          bg: 'bg-blue-50',
          label:
            translations?.linkingTableRow?.status?.waitingIdentification ||
            'Waiting Identification',
        };
      case 'unlinked':
        return {
          icon: Clock,
          color: 'text-gray-500',
          bg: 'bg-gray-100',
          label: translations?.linkingTableRow?.status?.unlinked || 'Not Linked',
        };
      default:
        return {
          icon: Clock,
          color: 'text-yellow-500',
          bg: 'bg-yellow-50',
          label: translations?.linkingTableRow?.status?.pending || 'Pending',
        };
    }
  };

  const status = getStatusConfig(row.status);
  const StatusIcon = status.icon;
  const guideLabel = row.guideName || translations?.linkingTableRow?.labels?.notLinked || 'Not linked';
  const organizerLabel = row.organizerName || translations?.linkingTableRow?.labels?.notLinked || 'Not linked';

  return (
    <tr className="hover:bg-gray-50">
      {/* Pilgrim Info */}
      <td className="px-6 py-4">
        <div className="flex items-center gap-3">
          <div
            className={`p-2 rounded-full ${
              row.gender === 'male' ? 'bg-blue-50' : row.gender === 'female' ? 'bg-pink-50' : 'bg-gray-50'
            }`}
          >
            <User
              className={
                row.gender === 'male'
                  ? 'text-blue-500'
                  : row.gender === 'female'
                    ? 'text-pink-500'
                    : 'text-gray-500'
              }
              size={20}
            />
          </div>
          <div>
            <div className="font-medium">{row.pilgrimName}</div>
            <div className="text-sm text-gray-500">
              {translations?.linkingTableRow?.labels?.passport || 'Passport'}:{' '}
              {row.passportNumber}
            </div>
            <div className="text-sm text-gray-500">
              {row.residenceCountry || translations?.common?.notSpecified || 'Not Specified'}
            </div>
          </div>
        </div>
      </td>

      {/* Guide Info */}
      <td className="px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-full bg-primary-50">
            <User className="text-primary-500" size={20} />
          </div>
          <div className={`font-medium ${row.isLinked ? 'text-gray-900' : 'text-gray-500'}`}>
            {guideLabel}
          </div>
        </div>
      </td>

      {/* Organizer Info */}
      <td className="px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-full bg-secondary-50">
            <Building2 className="text-secondary-500" size={20} />
          </div>
          <div className={`font-medium ${row.isLinked ? 'text-gray-900' : 'text-gray-500'}`}>
            {organizerLabel}
          </div>
        </div>
      </td>

      {/* Status */}
      <td className="px-6 py-4">
        <span
          className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-sm ${status.bg} ${status.color}`}
        >
          <StatusIcon size={16} />
          {status.label}
        </span>
      </td>
    </tr>
  );
};
